'use client'

import { useState, useRef, useEffect } from 'react'

import axios from '@/utils/assessmentAxiosInterceptor'
import { CSSTransition } from 'react-transition-group'

import LoginForm from './LoginForm'
import RegisterForm from './RegisterForm'
import VerifyForm from './VerifyForm'
import ForgottenPasswordForm from './ForgottenPasswordForm'

import Logo from '@/images/logo.svg'

import styles from './Auth.module.scss'
import classNames from 'classnames/bind'
const classes = classNames.bind(styles)

export default ({ type }) => {
    const [selectedType, setSelectedType] = useState(type)

    const loginNodeRef = useRef(null)
    const registerNodeRef = useRef(null)
    const resetNodeRef = useRef(null)
    const verificationNodeRef = useRef(null)

    const [emailParam, setEmailParam] = useState(null)
    const [firstNameParam, setFirstNameParam] = useState(null)
    const [lastNameParam, setLastNameParam] = useState(null)

    const [error, setError] = useState(null)
    const [email, setEmail] = useState(null)
    const [password, setPassword] = useState(null)

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search)
            setEmailParam(params.get('email'))
            setFirstNameParam(params.get('firstName'))
            setLastNameParam(params.get('lastName'))
        }
    }, [])

    useEffect(() => {
        setSelectedType(emailParam ? 'register' : type)
    }, [emailParam, type])

    const verificationCodeFunction = (code, newPassword) => {
        switch (selectedType) {
            case 'password-reset':
                return resetPassword(code, newPassword)
            case 'recaptcha-verify':
                return sendRecaptchaValidation(code)
            default:
                return sendVerification(code)
        }
    }
    useEffect(() => {
        setError(null)
    }, [selectedType])

    const sendVerification = async (code) => {
        try {
            // Post the request to the server.
            const res = await axios.post('/assessment/api/auth/confirmRegister', {
                verificationCode: code,
                email,
                password,
            })

            if (!res.data.success) {
                setError(req.data.message)
            }
            // If the request succeeds redirect to the main form.
            window.location = res?.data?.redirect ? res.data.redirect : '/'
        } catch (e) {
            if (e?.response?.data?.redirectToSignIn) {
                setError(e.response.data.message)

                // Attempt to login automatically.
                try {
                    const { data } = await axios.post('/assessment/api/auth/login', {
                        email,
                        password,
                    })

                    window.location = '/'
                    return
                } catch (e) {}
            }
            console.log(e)
            if (e?.response?.data) {
                setError(e.response.data.message)
            }
        }
    }

    const sendRecaptchaValidation = async (code) => {
        try {
            // Post the request to the server.
            const res = await axios.post('/assessment/api/auth/captchaCodeVerification', {
                code,
                email,
                password,
            })

            if (!res.data.success) {
                setError(req.data.message)
            }
            // If the request succeeds redirect to the main form.
            window.location = res?.data?.redirect ? res.data.redirect : '/'
        } catch (e) {
            console.log(e)
            if (e?.response?.data) {
                setError(e.response.data.message)
            }
        }
    }

    const resetPassword = async (code, password) => {
        setError(null)
        try {
            // Post the request to the server.
            const res = await axios.post('/assessment/api/auth/resetPassword', {
                code,
                email,
                password,
            })

            if (!res.data.success) {
                alert(req.data.message)
            }

            // If the request succeeds redirect to the main form.
            window.location = '/'
        } catch (e) {
            if (e?.response?.data) {
                setError(e.response.data.message)
            }
        }
    }

    const resendRequest = async () => {
        if (selectedType == 'password-reset') {
            try {
                grecaptcha.ready(() => {
                    grecaptcha
                        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, { action: 'submit' })
                        .then(async (token) => {
                            try {
                                const { data } = await axios.post('/assessment/api/auth/forgottenPassword', {
                                    email,
                                    captchaToken: token,
                                })
                            } catch (e) {
                                if (e?.response?.data) {
                                    setError(e.response.data.message)
                                }
                            }
                        })
                })
            } catch (e) {
                if (e?.response?.data) {
                    setError(e.response.data.message)
                }
            }
        } else {
            try {
                // Post the request to the server.
                const res = await axios.post('/assessment/api/auth/resendConfirmation', {
                    email,
                })
            } catch (e) {
                console.log(e)
                if (e?.response?.data) {
                    setError(e.response.data.message)
                }
            }
        }
    }

    return (
        <div className={classes('wrapper')}>
            <Logo className={classes('logo')} />
            <div className={classes('graphic-wrapper')}></div>
            <div className={classes('form-wrapper')}>
                <CSSTransition
                    in={selectedType == 'login' && email == null}
                    timeout={200}
                    classNames={{
                        enter: classes('enter'),
                        enterActive: classes('enter-active'),
                        exit: classes('exit'),
                        exitActive: classes('exit-active'),
                    }}
                    unmountOnExit
                    nodeRef={loginNodeRef}
                >
                    <div className={classes('transition-wrap')} ref={loginNodeRef}>
                        <div className={classes('container')}>
                            <LoginForm
                                setEmail={setEmail}
                                setPassword={setPassword}
                                setSelectedType={setSelectedType}
                                setError={setError}
                            />

                            {error !== null && (
                                <div className={classes('error-container')}>
                                    <p>{error}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </CSSTransition>
                <CSSTransition
                    in={selectedType == 'register' && email == null}
                    timeout={200}
                    classNames={{
                        enter: classes('enter'),
                        enterActive: classes('enter-active'),
                        exit: classes('exit'),
                        exitActive: classes('exit-active'),
                    }}
                    unmountOnExit
                    nodeRef={registerNodeRef}
                >
                    <div className={classes('transition-wrap')} ref={registerNodeRef}>
                        <div className={classes('container')}>
                            <RegisterForm
                                setEmail={setEmail}
                                setPassword={setPassword}
                                setSelectedType={setSelectedType}
                                setError={setError}
                                firstNameParam={firstNameParam}
                                lastNameParam={lastNameParam}
                                emailParam={emailParam}
                            />

                            {error !== null && (
                                <div className={classes('error-container')}>
                                    <p>{error}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </CSSTransition>
                <CSSTransition
                    in={selectedType == 'password-reset' && !email}
                    timeout={200}
                    classNames={{
                        enter: classes('enter'),
                        enterActive: classes('enter-active'),
                        exit: classes('exit'),
                        exitActive: classes('exit-active'),
                    }}
                    unmountOnExit
                    nodeRef={resetNodeRef}
                >
                    <div className={classes('transition-wrap')} ref={resetNodeRef}>
                        <div className={classes('container')}>
                            <ForgottenPasswordForm
                                setEmail={setEmail}
                                setError={setError}
                                setSelectedType={setSelectedType}
                            />

                            {error !== null && (
                                <div className={classes('error-container')}>
                                    <p>{error}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </CSSTransition>
                <CSSTransition
                    in={email != null}
                    timeout={300}
                    classNames={{
                        enter: classes('enter'),
                        enterActive: classes('enter-active'),
                        exit: classes('exit'),
                        exitActive: classes('exit-active'),
                    }}
                    unmountOnExit
                    nodeRef={verificationNodeRef}
                >
                    <div className={classes('transition-wrap')} ref={verificationNodeRef}>
                        <div className={classes('container')}>
                            <VerifyForm
                                selectedType={selectedType}
                                setError={setError}
                                setPassword={setPassword}
                                sendRequest={verificationCodeFunction}
                                resendRequest={resendRequest}
                            />

                            {error !== null && (
                                <div className={classes('error-container')}>
                                    <p>{error}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </CSSTransition>
            </div>
        </div>
    )
}
